import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { learningAPI, notificationAPI } from '../services/api';

const iconByType = {
  announcement: <CampaignRoundedIcon />,
  assignment: <ChecklistRoundedIcon />,
  feedback: <ForumRoundedIcon />,
  system: <NotificationsNoneRoundedIcon />,
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState(null);
  const [announcementLoading, setAnnouncementLoading] = useState(false);
  const [announcementError, setAnnouncementError] = useState('');

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationAPI.list({ limit: 100 });
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unreadCount || 0);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const announcementId = searchParams.get('announcement');
  useEffect(() => {
    if (!announcementId) {
      setAnnouncement(null);
      setAnnouncementError('');
      return;
    }
    let active = true;
    setAnnouncementLoading(true);
    learningAPI.getAnnouncement(announcementId)
      .then((response) => {
        if (!active) return;
        setAnnouncement(response.data?.announcement || response.data || null);
        setAnnouncementError('');
      })
      .catch((requestError) => {
        if (!active) return;
        setAnnouncement(null);
        setAnnouncementError(requestError.response?.data?.message || 'Unable to load this announcement.');
      })
      .finally(() => {
        if (active) setAnnouncementLoading(false);
      });
    return () => {
      active = false;
    };
  }, [announcementId]);

  const openNotification = async (notification) => {
    if (!notification.readAt) {
      try {
        await notificationAPI.markRead(notification.id);
        setUnreadCount((current) => Math.max(current - 1, 0));
        setNotifications((current) => current.map((item) => (
          item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item
        )));
        window.dispatchEvent(new Event('virtual-medic:notifications-changed'));
      } catch (requestError) {
        setError(requestError.response?.data?.message || 'Unable to update the notification.');
      }
    }
    if (notification.link && notification.link !== '/notifications') {
      navigate(notification.link);
    } else if (notification.type === 'announcement' && notification.metadata?.announcementId) {
      navigate(`/notifications?announcement=${notification.metadata.announcementId}`);
    }
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setUnreadCount(0);
      setNotifications((current) => current.map((item) => ({
        ...item,
        readAt: item.readAt || new Date().toISOString(),
      })));
      window.dispatchEvent(new Event('virtual-medic:notifications-changed'));
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to update notifications.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>Notifications</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Tutor feedback, assigned cases, and university announcements in one place.
          </Typography>
        </Box>
        {unreadCount > 0 && (
          <Button variant="outlined" onClick={markAllRead} sx={{ alignSelf: { sm: 'flex-start' } }}>
            Mark all read
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
      ) : notifications.length ? (
        <Stack spacing={1.25}>
          {notifications.map((notification) => (
            <Paper
              component="button"
              type="button"
              key={notification.id}
              variant="outlined"
              onClick={() => openNotification(notification)}
              sx={{
                p: 2,
                width: '100%',
                textAlign: 'left',
                borderRadius: 3,
                cursor: notification.link && notification.link !== '/notifications' ? 'pointer' : 'default',
                bgcolor: notification.readAt ? 'background.paper' : 'rgba(49, 182, 228, 0.07)',
                borderColor: notification.readAt ? 'divider' : 'primary.light',
                font: 'inherit',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ color: 'primary.main', mt: 0.25 }}>{iconByType[notification.type] || iconByType.system}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Typography sx={{ fontWeight: notification.readAt ? 700 : 900 }}>
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatDateTime(notification.createdAt)}
                    </Typography>
                  </Stack>
                  {notification.message && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                      {notification.message}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                    <Chip size="small" label={notification.type || 'system'} sx={{ textTransform: 'capitalize' }} />
                    {!notification.readAt && <Chip size="small" color="primary" label="Unread" />}
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Paper variant="outlined" sx={{ py: 8, px: 3, textAlign: 'center', borderRadius: 3 }}>
          <NotificationsNoneRoundedIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 800 }}>No notifications yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            New messages will appear here.
          </Typography>
        </Paper>
      )}

      <Dialog
        open={Boolean(announcementId)}
        onClose={() => setSearchParams({}, { replace: true })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>University announcement</DialogTitle>
        <DialogContent>
          {announcementLoading ? (
            <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
          ) : announcementError ? (
            <Alert severity="error">{announcementError}</Alert>
          ) : announcement ? (
            <Stack spacing={1.5}>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>{announcement.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                Published {formatDateTime(announcement.publishAt || announcement.createdAt)}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>
                {announcement.body}
              </Typography>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchParams({}, { replace: true })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default NotificationsPage;
